import { apiClient } from './client';
import { FinalInspection, FinalInspectionPayload } from '../types/finalInspection';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchFinalInspections(params?: {
  inventoryId?: number;
  contractId?: number;
  keyword?: string;
}): Promise<FinalInspection[]> {
  const { data } = await apiClient.get<FinalInspection[]>('/final-inspections', {
    params: {
      ...(params?.inventoryId != null ? { inventoryId: params.inventoryId } : {}),
      ...(params?.contractId != null ? { contractId: params.contractId } : {}),
      ...(params?.keyword?.trim() ? { q: params.keyword.trim() } : {}),
    },
  });
  return data;
}

export async function fetchNextFinalLotNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/final-inspections/suggestions/lot-no'
  );
  return data;
}
export async function fetchFinalInspection(id: number): Promise<FinalInspection> {
  return (await apiClient.get<FinalInspection>(`/final-inspections/${id}`)).data;
}
export async function createFinalInspection(payload: FinalInspectionPayload): Promise<FinalInspection> {
  return (await apiClient.post<FinalInspection>('/final-inspections', payload)).data;
}
export async function updateFinalInspection(
  id: number,
  payload: Partial<FinalInspectionPayload>
): Promise<FinalInspection> {
  return (await apiClient.patch<FinalInspection>(`/final-inspections/${id}`, payload)).data;
}
export async function deleteFinalInspection(id: number): Promise<void> {
  await apiClient.delete(`/final-inspections/${id}`);
}
export async function approveFinalInspection(id: number): Promise<FinalInspection> {
  return (await apiClient.post<FinalInspection>(`/final-inspections/${id}/approve`)).data;
}

export async function revokeFinalInspection(id: number): Promise<FinalInspection> {
  return (await apiClient.post<FinalInspection>(`/final-inspections/${id}/revoke`)).data;
}

export async function downloadFinalInspectionPdf(
  inspectionId: number,
  fileName?: string
): Promise<void> {
  try {
    const { data } = await apiClient.get<Blob>(`/final-inspections/${inspectionId}/pdf`, {
      responseType: 'blob',
    });
    if (data.type && data.type.includes('application/json')) {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string };
      throw new Error(parsed.message || 'PDF 다운로드에 실패했습니다.');
    }
    const url = window.URL.createObjectURL(data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName || `제품검사성적서_${inspectionId}.pdf`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const blob = (err as { response?: { data?: Blob } })?.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed.message) {
          throw new Error(parsed.message);
        }
      } catch (inner) {
        if (inner instanceof Error && inner.message && !inner.message.startsWith('Unexpected')) {
          throw inner;
        }
      }
    }
    if (err instanceof Error && err.message) {
      throw err;
    }
    throw err;
  }
}

export async function uploadFinalInspectionAttachment(
  inspectionId: number,
  file: File
): Promise<FinalInspection> {
  const body = new FormData();
  body.append('file', file);
  const { data } = await apiClient.post<FinalInspection>(
    `/final-inspections/${inspectionId}/attachments`,
    body,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteFinalInspectionAttachment(
  inspectionId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/final-inspections/${inspectionId}/attachments/${attachmentId}`);
}

export async function downloadFinalInspectionAttachment(
  inspectionId: number,
  attachmentId: number
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/final-inspections/${inspectionId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  return data;
}
